import {
	CaretDoubleLeft,
	CaretDoubleRight,
	CaretLeft,
	CaretRight,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

type DataTablePaginationProps = {
	page: number;
	pageSize: number;
	count: number;
	onPageChange: (page: number) => void;
	onPageSizeChange: (size: number) => void;
	pageSizeOptions?: number[];
	hasPrevious: boolean;
	hasNext: boolean;
};

export function DataTablePagination({
	page,
	pageSize,
	count,
	onPageChange,
	onPageSizeChange,
	pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
	hasPrevious,
	hasNext,
}: DataTablePaginationProps) {
	const { t } = useTranslation();
	const totalPages = Math.max(1, Math.ceil(count / pageSize));
	const from = count === 0 ? 0 : (page - 1) * pageSize + 1;
	const to = Math.min(page * pageSize, count);

	return (
		<div className="flex shrink-0 items-center justify-between">
			<div className="text-muted-foreground flex items-center gap-3 text-sm">
				<div className="flex items-center gap-2">
					<span>{t("dataTable.rows")}</span>
					<Select
						value={String(pageSize)}
						onValueChange={(v) => onPageSizeChange(Number(v))}
					>
						<SelectTrigger className="h-8 w-20">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{pageSizeOptions.map((n) => (
								<SelectItem key={n} value={String(n)}>
									{n}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<span>{t("dataTable.pagination", { from, to, count })}</span>
			</div>

			{count > pageSize && (
				<div className="flex items-center gap-1">
					<Button
						variant="outline"
						size="icon"
						aria-label={t("dataTable.firstPage")}
						disabled={page === 1}
						onClick={() => onPageChange(1)}
					>
						<CaretDoubleLeft className="size-4" aria-hidden="true" />
					</Button>
					<Button
						variant="outline"
						size="icon"
						aria-label={t("dataTable.prevPage")}
						disabled={!hasPrevious}
						onClick={() => onPageChange(page - 1)}
					>
						<CaretLeft className="size-4" aria-hidden="true" />
					</Button>
					<Button
						variant="outline"
						size="icon"
						aria-label={t("dataTable.nextPage")}
						disabled={!hasNext}
						onClick={() => onPageChange(page + 1)}
					>
						<CaretRight className="size-4" aria-hidden="true" />
					</Button>
					<Button
						variant="outline"
						size="icon"
						aria-label={t("dataTable.lastPage")}
						disabled={page === totalPages}
						onClick={() => onPageChange(totalPages)}
					>
						<CaretDoubleRight className="size-4" aria-hidden="true" />
					</Button>
				</div>
			)}
		</div>
	);
}
